import React, {useEffect, useState} from "react";
import {useAuth0} from "@auth0/auth0-react";
import {useNavigate} from "react-router-dom";
import { useQuery } from "react-query";
import usePageView from "../../../../hooks/usePageView/usePageView";
import Error from "../../../../components/Error/Error";
import useAuthentication from "../../../../hooks/useAuthentication/useAuthentication";
import Loading from "../../../../components/Loading/Loading";
import {getProfile, logDiagnosticEvent} from "../../../../api/queries";

function TherapistRegistration() {
  const [error, setError] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    loginWithRedirect,
    getAccessTokenSilently,
    isLoading,
    error: auth0Error,
  } = useAuth0();
  const setUser = useAuthentication((s) => s.setUser);

  const { isLoading: isAccountProfileLoading, isError: isAccountProfileError, data: accountProfileData } = useQuery(
    "account-profile",
    getProfile,
    {
      cacheTime: 0,
      enabled: isAuthReady,
      retry: false,
    });

  useEffect(() => {
    (async() => {
      try {
        if (!isLoading) {
          // TODO remove this logging after issue is diagnosed and fixed
          const redirectTime = sessionStorage.getItem("auth0_redirect_time");
          const isRecentRedirect = redirectTime && Date.now() - Number(redirectTime) < 5000;
          const diagnosticDescription = isRecentRedirect ? [
            `auth0Error=${auth0Error?.message ?? "none"}`,
            `userSub=${user?.sub ?? "none"}`,
            `userThriveSub=${user?.thrive_sub ?? "none"}`,
            `localStorageKeys=${Object.keys(localStorage).join(",")}`,
          ].join(";") : "";

          if (isAuthenticated) {
            if (isRecentRedirect) {
              await logDiagnosticEvent("AUTH0_LOOP", diagnosticDescription, "SUCCESS");
            }
            sessionStorage.removeItem("auth0_redirect_time");

            const token = await getAccessTokenSilently();
            setUser({
              sub: user?.thrive_sub ?? null,
              accessToken: token ?? null,
            });
            setIsAuthReady(true);
          } else {
            if (isRecentRedirect) {
              await logDiagnosticEvent("AUTH0_LOOP", diagnosticDescription, "FAILURE");
              // Clear stale auth state to break the loop
              localStorage.clear();
            }
            sessionStorage.setItem("auth0_redirect_time", String(Date.now()));

            await loginWithRedirect({
              scope: "openid profile email phone",
              redirectUri: `https://${window.location.host}/therapist/register`
            });
          }
        }
      } catch (e) {
        setError(true);
      }
    })();
  }, [isLoading, isAuthenticated, user]);

  useEffect(() => {
    (async () => {
      try {
        if (isAuthReady && !isAccountProfileLoading && accountProfileData) {
          if (accountProfileData.client) {
            // eslint-disable-next-line no-alert -- a client account that signs in here gets a one-time choice before any redirect, and this page renders only a loading spinner, so a native dialog is acceptable
            if (window.confirm("Welcome! It looks like you have a client account with us. Would you like to switch to the Alli\u00A0Connect client app instead?")) {
              navigate("/client");
            } else {
              navigate("/account/therapist/logout-client");
            }
          } else {
            navigate("/account/therapist/edit-profile");
          }
        }
      } catch (e) {
        setError(true);
      }
    })();
  }, [isAuthReady, isAccountProfileLoading, accountProfileData]);

  usePageView({path: "/app/therapist/registration"});

  if (error || isAccountProfileError) {
    return <Error centered />;
  }

  return <Loading />;
}

export default TherapistRegistration;
